import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import SafeChart from '../components/SafeChart';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import PerformanceBadge from '../components/PerformanceBadge';
import FitnessProgressView from '../components/FitnessProgressView';
import BackfillPreviewCard from '../components/BackfillPreviewCard';
import { normalizeProgress } from '../lib/assessmentNormalizer';
import { CONTRACT_FIXTURE_ATHLETE_IDS, shouldLimitForContractFixture } from '../lib/visibility';
import { Pencil, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category colors for charts. Keep legacy "Strength" / "Resistance"
// keys so historical records rendered on detail views still pick up
// a colour after the Phase-1 split.
const CATEGORY_COLORS = {
  'Lower Body Strength': '#8b5cf6',
  'Upper Body Strength': '#7c3aed',
  Speed: '#3b82f6',
  Agility: '#10b981',
  Power: '#f59e0b',
  Endurance: '#ef4444',
  Flexibility: '#ec4899',
  // Legacy buckets (pre-Phase-1) kept for backward-compat rendering.
  Strength: '#8b5cf6',
  Resistance: '#ef4444',
};

// Phase-1: reps and manual score are no longer entered via the staff
// form. Detection helpers below cope with both canonical and legacy
// category names so the form renders cleanly for mixed data sets.
const STRENGTH_CATEGORIES = new Set([
  'Lower Body Strength',
  'Upper Body Strength',
  'Strength', // legacy
]);
const TIME_CATEGORIES = new Set(['Speed', 'Agility']);
const DISTANCE_CATEGORIES = new Set(['Endurance', 'Resistance']);
const MEASUREMENT_CATEGORIES = new Set(['Power', 'Flexibility']);

const CATEGORY_UNIT = {
  'Lower Body Strength': 'kg',
  'Upper Body Strength': 'kg',
  Strength: 'kg',
  Speed: 'seconds',
  Agility: 'seconds',
  Power: 'cm',
  Endurance: 'meters',
  Resistance: 'meters',
  Flexibility: 'cm',
};

// Shuttle Run Test is the one Endurance test that doesn't share Cooper
// Test's distance/meters shape — it records whole laps. Per-test
// overrides (not per-category) keep Cooper Test unaffected.
const LAPS_TESTS = new Set(['Shuttle Run Test']);
const TEST_UNIT_OVERRIDE = { 'Shuttle Run Test': 'laps' };

// Parse a whole-number lap count. Rejects decimals, negatives and
// non-numeric input. Empty input returns null (not recorded — never 0).
const parseLapsSafe = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = Number(String(raw).trim());
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return null;
  return n;
};

// Parse a decimal string accepting comma OR dot as separator and
// rejecting negatives. Empty / invalid inputs return null so the
// backend payload stays clean.
const parseDecimalSafe = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const cleaned = String(raw).trim().replace(',', '.');
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

const performanceCategoryLabel = (value) => {
  // Legacy rows: a manual 0–10 `score` existed but no category.
  if (value) return value;
  return 'Pending Calculation';
};

const StaffAssessments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Phase 81 — accept ?athlete=<id> deep-link from Athlete 360 cards.
  const [searchParams] = useSearchParams();
  
  const [categories, setCategories] = useState({});
  const [athletes, setAthletes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [athleteProgress, setAthleteProgress] = useState(null);
  // Phase 82.3 — season-scope toggle for the assessment history view +
  // the carry-forward baseline payload (latest pre-season session).
  const [fitSeasonScope, setFitSeasonScope] = useState('current');
  const [fitBaseline, setFitBaseline] = useState(null);
  const [fitBaselineSeason, setFitBaselineSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [newSessionDialog, setNewSessionDialog] = useState(false);
  const [recordResultsDialog, setRecordResultsDialog] = useState(false);
  const [viewSessionDialog, setViewSessionDialog] = useState(false);
  
  // Form states
  const [newSession, setNewSession] = useState({
    athlete_id: '',
    session_date: new Date().toISOString().split('T')[0],
    evaluator_notes: ''
  });
  
  const [results, setResults] = useState({});
  // Phase 2: live classification preview, keyed by test_type.
  // { label, colorKey, reason }. Server is the source of truth.
  const [livePreviews, setLivePreviews] = useState({});
  const previewTimers = useRef({});

  /**
   * Fire a debounced `/api/assessments/classify` call for a single
   * test row. Populates `livePreviews[test_type]` so the Performance
   * Category badge reflects the server's verdict in near-real-time.
   */
  const schedulePreview = (test_type, raw_value) => {
    if (!selectedSession?.athlete_id && !selectedSession?.athlete?.id) return;
    const athleteId = selectedSession.athlete_id || selectedSession.athlete?.id;
    const date_iso = selectedSession.session_date
      || new Date().toISOString().split('T')[0];
    const numeric = parseDecimalSafe(raw_value);
    // Clear any pending timer for this test row.
    clearTimeout(previewTimers.current[test_type]);
    if (numeric === null) {
      // Not enough data — revert to pending-calculation pill.
      setLivePreviews(prev => {
        const cp = { ...prev };
        delete cp[test_type];
        return cp;
      });
      return;
    }
    previewTimers.current[test_type] = setTimeout(async () => {
      try {
        const resp = await axios.post(`${API}/assessments/classify`, {
          athlete_id: athleteId,
          test_type,
          result_value: numeric,
          assessment_date: date_iso,
        });
        setLivePreviews(prev => ({
          ...prev,
          [test_type]: {
            label: resp.data.display_label,
            colorKey: resp.data.color_key,
            reason: resp.data.reason,
          },
        }));
      } catch (_err) {
        // Silent: the form still submits; server recomputes on save.
      }
    }, 350);
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount — cancel any pending preview requests.
      Object.values(previewTimers.current).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (!['Admin', 'Staff'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    fetchInitialData();
  }, [user, navigate]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, athletesRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/assessments/categories`),
        axios.get(`${API}/athletes`),
        axios.get(`${API}/assessments/sessions`)
      ]);
      setCategories(categoriesRes.data);
      setAthletes(athletesRes.data);
      // Phase 81 — honour ?athlete=<id> deep-link from Athlete 360.
      // Set unconditionally; visibility filter may strip contract fixtures.
      const deepLinkAid = searchParams.get('athlete') || searchParams.get('athlete_id');
      if (deepLinkAid) setSelectedAthlete(deepLinkAid);
      // Staff sessions list is global. Hide Test Player's sessions from Admin/Staff
      // to keep the demo list clean (fixture is still visible to Player/Parent roles
      // via their own pages).
      const allSessions = sessionsRes.data || [];
      const role = (typeof window !== 'undefined' ? localStorage.getItem('userRole') : null);
      const hideForRole = role === 'Admin' || role === 'Staff';
      const filteredSessions = hideForRole
        ? allSessions.filter(s => !CONTRACT_FIXTURE_ATHLETE_IDS.has(s.athlete_id))
        : allSessions;
      setSessions(filteredSessions);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAthleteProgress = async (athleteId) => {
    try {
      const [historyRes, baselineRes] = await Promise.all([
        axios.get(`${API}/assessments/athlete/${athleteId}/history?season_scope=${fitSeasonScope}`),
        // Phase 82.3 — Pre-season baseline. 403 swallowed silently.
        axios.get(`${API}/assessments/athlete/${athleteId}/baseline`).catch(() => ({ data: null })),
      ]);
      let data = historyRes.data;
      // UX guard: Admin/Staff viewing Test Player see only the 3 most recent sessions.
      if (shouldLimitForContractFixture(athleteId) && Array.isArray(data?.history) && data.history.length > 3) {
        const sortedHistory = [...data.history].sort((a, b) =>
          new Date(b.session_date || b.date || 0).getTime() - new Date(a.session_date || a.date || 0).getTime(),
        ).slice(0, 3);
        data = { ...data, history: sortedHistory };
      }
      setAthleteProgress({
        ...data,
        progress: normalizeProgress(data.progress, categories),
      });
      setFitBaseline(baselineRes?.data?.baseline_session || null);
      setFitBaselineSeason(baselineRes?.data?.season || null);
    } catch (error) {
      toast.error('Failed to fetch athlete progress');
    }
  };

  // Re-fetch history whenever the scope toggle changes.
  useEffect(() => {
    if (selectedAthlete) fetchAthleteProgress(selectedAthlete);
  }, [fitSeasonScope, selectedAthlete]);

  const handleCreateSession = async () => {
    if (!newSession.athlete_id || !newSession.session_date) {
      toast.error('Please select an athlete and date');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/assessments/sessions`, newSession);
      toast.success('Assessment session created');
      setNewSessionDialog(false);
      setNewSession({
        athlete_id: '',
        session_date: new Date().toISOString().split('T')[0],
        evaluator_notes: ''
      });
      fetchInitialData();
      
      // Open record results dialog for the new session
      setSelectedSession({ id: response.data.session_id, athlete_name: response.data.athlete_name });
      initializeResults();
      setRecordResultsDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create session');
    }
  };

  const initializeResults = () => {
    const initialResults = {};
    Object.entries(categories).forEach(([category, data]) => {
      // Skip metadata keys (e.g. `_legacy_categories`, `_legacy_test_types`).
      if (!data || typeof data !== 'object' || !Array.isArray(data.tests)) return;
      data.tests.forEach(test => {
        initialResults[test] = {
          category,
          test_type: test,
          weight: '',
          // `reps` and `score` left on the shape for legacy record edits;
          // the staff form no longer collects them.
          reps: '',
          score: '',
          time: '',
          measurement: '',
          distance: '',
          laps: '',
          performance_category: null,
          notes: ''
        };
      });
    });
    setResults(initialResults);
  };

  const handleRecordResults = async () => {
    if (!selectedSession) return;

    const invalidLapsTests = Object.values(results).filter(r => {
      const raw = r.laps;
      return raw !== '' && raw !== null && raw !== undefined && parseLapsSafe(raw) === null;
    }).map(r => r.test_type);
    if (invalidLapsTests.length > 0) {
      toast.error(`${invalidLapsTests.join(', ')}: laps must be a whole number ≥ 0`);
      return;
    }

    const resultsToSubmit = Object.values(results).filter(r => {
      return r.weight || r.time || r.measurement || r.distance || r.laps;
    }).map(r => ({
      session_id: selectedSession.id,
      test_type: r.test_type,
      category: r.category,
      weight: parseDecimalSafe(r.weight),
      // Phase 1: reps + manual score are NOT collected. Pass null so
      // the backend does not accidentally persist stale form state.
      reps: null,
      score: null,
      time: parseDecimalSafe(r.time),
      measurement: parseDecimalSafe(r.measurement),
      distance: parseDecimalSafe(r.distance),
      laps: parseLapsSafe(r.laps),
      performance_category: null, // Phase 2 engine fills this later.
      notes: r.notes || null
    }));

    if (resultsToSubmit.length === 0) {
      toast.error('Please enter at least one result');
      return;
    }

    try {
      await axios.post(`${API}/assessments/results/bulk`, {
        session_id: selectedSession.id,
        results: resultsToSubmit
      });
      toast.success('Results recorded successfully');
      setRecordResultsDialog(false);
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record results');
    }
  };

  const openViewSession = async (session) => {
    try {
      const response = await axios.get(`${API}/assessments/sessions/${session.id}`);
      setSelectedSession(response.data);
      setViewSessionDialog(true);
    } catch (error) {
      toast.error('Failed to fetch session details');
    }
  };

  const openEditSession = async (session) => {
    try {
      const response = await axios.get(`${API}/assessments/sessions/${session.id}`);
      const full = response.data;
      setSelectedSession({ ...full, athlete_name: full?.athlete?.full_name || session.athlete_name });
      // Pre-populate `results` from existing saved values so editing feels seamless.
      const initial = {};
      Object.entries(categories).forEach(([category, data]) => {
        if (!data || typeof data !== 'object' || !Array.isArray(data.tests)) return;
        data.tests.forEach(test => {
          initial[test] = {
            category, test_type: test,
            weight: '', reps: '', score: '', time: '', measurement: '', distance: '', laps: '',
            performance_category: null, notes: ''
          };
        });
      });
      Object.values(full.results || {}).forEach(arr => {
        (arr || []).forEach(r => {
          if (initial[r.test_type]) {
            initial[r.test_type] = {
              category: r.category, test_type: r.test_type,
              weight: r.weight ?? '', reps: r.reps ?? '', score: r.score ?? '',
              time: r.time ?? '', measurement: r.measurement ?? '', distance: r.distance ?? '',
              laps: r.laps ?? '',
              performance_category: r.performance_category ?? null,
              notes: r.notes ?? ''
            };
          }
        });
      });
      setResults(initial);
      setRecordResultsDialog(true);
    } catch (error) {
      toast.error('Failed to load session for editing');
    }
  };

  const handleDeleteSession = async (session) => {
    if (!window.confirm(`Delete assessment session for ${session.athlete_name || 'this athlete'} on ${session.session_date}? This removes all recorded results too.`)) return;
    try {
      await axios.delete(`${API}/assessments/sessions/${session.id}`);
      toast.success('Assessment session deleted');
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete session');
    }
  };

  const handleAthleteSelect = (athleteId) => {
    setSelectedAthlete(athleteId);
    if (athleteId) {
      fetchAthleteProgress(athleteId);
    } else {
      setAthleteProgress(null);
    }
  };

  const updateResult = (testType, field, value) => {
    setResults(prev => ({
      ...prev,
      [testType]: {
        ...prev[testType],
        [field]: value
      }
    }));
    // Trigger a live classification preview when the result value
    // changes (ignore non-numeric field edits like `notes`).
    if (['weight', 'time', 'measurement', 'distance', 'laps'].includes(field)) {
      schedulePreview(testType, value);
    }
  };

  const getResultValue = (result) => {
    // Phase-1-aware, legacy-tolerant value formatter. Note: the old
    // Strength renderer showed `{weight}kg x {reps}`. Reps have been
    // retired from the new staff form and are no longer shown here,
    // even for legacy records that still carry them in the DB.
    if (result.test_type === 'Shuttle Run Test') {
      return (result.laps !== null && result.laps !== undefined && result.laps !== '')
        ? `${result.laps} laps` : '-';
    }
    if (STRENGTH_CATEGORIES.has(result.category)) {
      return result.weight ? `${result.weight}kg` : '-';
    }
    if (TIME_CATEGORIES.has(result.category)) {
      return result.time ? `${result.time}s` : '-';
    }
    if (DISTANCE_CATEGORIES.has(result.category)) {
      return result.distance ? `${result.distance}m` : '-';
    }
    return result.measurement ? `${result.measurement}cm` : '-';
  };

  const renderProgressChart = (testName, data) => {
    if (!data || data.history.length < 2) return null;
    
    const chartData = data.history.map(h => ({
      date: h.date,
      value: h.value
    }));
    
    return (
      <div className="h-48">
        <SafeChart height={192}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" fontSize={10} />
            <YAxis fontSize={10} />
            <Tooltip />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={CATEGORY_COLORS[data.category]} 
              strokeWidth={2}
              dot={{ fill: CATEGORY_COLORS[data.category] }}
            />
          </LineChart>
        </SafeChart>
      </div>
    );
  };

  if (!['Admin', 'Staff'].includes(user?.role)) return null;

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Fitness Assessments
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">Physical performance evaluations and progress tracking</p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/assessments/dashboard')}
              data-testid="fitness-dashboard-link"
            >
              Fitness Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/assessments/bulk')}
              data-testid="bulk-sessions-link"
            >
              Bulk Sessions
            </Button>
            {user?.role === 'Admin' && (
              <Button
                variant="outline"
                onClick={() => navigate('/assessments/standards')}
                data-testid="standards-review-link"
              >
                Standards Review
              </Button>
            )}
            <Button 
              className="bg-pfap-green hover:bg-pfap-green/90"
              onClick={() => {
                initializeResults();
                setNewSessionDialog(true);
              }}
              data-testid="new-assessment-btn"
            >
              + New Assessment
            </Button>
          </div>
        </div>

        {/* Admin-only Legacy Classification Backfill */}
        {user?.role === 'Admin' && <BackfillPreviewCard />}

        {/* Phase 76 — Player-first view (mirrors the Biometrics UX).
            The page is now anchored on a single athlete: pick one, then see
            their summary, history, and trends. The global recent-assessments
            feed lives in Fitness Dashboard. */}
        {(() => {
          // Athlete-specific selections derived from existing state — no new
          // backend endpoints needed for Phase 76.
          const selectedAthleteObj = athletes.find(a => a.id === selectedAthlete) || null;
          const athleteSessions = selectedAthlete
            ? sessions.filter(s => s.athlete_id === selectedAthlete)
            : [];
          const latestSession = athleteSessions[0] || null;  // sessions list is already date-desc

          return (
            <>
              {/* Athlete selector card — always visible */}
              <Card className="mb-6" data-testid="athlete-selector-card">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-4 flex-wrap">
                    <span>Athlete</span>
                    <div className="w-72">
                      <SearchableAthleteSelect
                        athletes={athletes}
                        value={selectedAthlete || ''}
                        onChange={handleAthleteSelect}
                        placeholder="Search or select an athlete…"
                        data-testid="athlete-select"
                      />
                    </div>
                  </CardTitle>
                </CardHeader>
              </Card>

              {!selectedAthlete ? (
                /* Phase 76 — clean empty state when no athlete is selected. */
                <Card data-testid="fitness-empty-state-card">
                  <CardContent className="py-12 text-center">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1"
                        data-testid="fitness-empty-title">
                      Select an athlete to view fitness assessments
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto"
                       data-testid="fitness-empty-description">
                      Choose an athlete to see their latest results, assessment history,
                      and progress trends.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
                      <Button
                        className="bg-pfap-green hover:bg-pfap-green/90"
                        onClick={() => { initializeResults(); setNewSessionDialog(true); }}
                        data-testid="empty-start-new-assessment-btn"
                      >
                        Start New Assessment
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/assessments/bulk')}
                        data-testid="empty-bulk-session-btn"
                      >
                        Create Bulk Session
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/assessments/dashboard')}
                        data-testid="empty-dashboard-btn"
                      >
                        Open Fitness Dashboard
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6" data-testid="athlete-fitness-view">
                  {/* Athlete header */}
                  <Card data-testid="athlete-fitness-header">
                    <CardContent className="py-4 flex items-start justify-between flex-wrap gap-3">
                      <div className="min-w-0">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100"
                            data-testid="athlete-fitness-name">
                          {selectedAthleteObj?.full_name || 'Athlete'}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {[selectedAthleteObj?.squad, selectedAthleteObj?.position].filter(Boolean).join(' · ') || '—'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Last assessment:{' '}
                          <span className="text-gray-700 dark:text-gray-200" data-testid="athlete-last-assessment-date">
                            {latestSession?.session_date || 'No assessments yet'}
                          </span>
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          className="bg-pfap-green hover:bg-pfap-green/90"
                          onClick={() => {
                            initializeResults();
                            setNewSession(s => ({ ...s, athlete_id: selectedAthlete }));
                            setNewSessionDialog(true);
                          }}
                          data-testid="athlete-add-assessment-btn"
                        >
                          + Add Assessment
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate('/assessments/bulk')}
                          data-testid="athlete-bulk-session-btn"
                        >
                          Bulk Session
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Phase 82.3 — Season scope + Baseline reference card */}
                  <div className="flex items-center justify-between flex-wrap gap-2" data-testid="fitness-scope-row">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {fitSeasonScope === 'current' ? 'Current Season Assessments' : 'Full Assessment History'}
                    </h3>
                    <div className="flex items-center gap-1.5" data-testid="fitness-season-scope">
                      {[
                        { key: 'current', label: 'Current Season' },
                        { key: 'all', label: 'Full History' },
                      ].map(opt => (
                        <button
                          key={opt.key}
                          onClick={() => setFitSeasonScope(opt.key)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                            fitSeasonScope === opt.key
                              ? 'bg-pfap-green text-white border-pfap-green'
                              : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                          }`}
                          data-testid={`fitness-scope-${opt.key}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {fitBaseline && (
                    <Card
                      className="border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/50"
                      data-testid="fitness-baseline-card"
                    >
                      <CardContent className="py-3">
                        <p className="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300 font-semibold">
                          Baseline / Last Season Reference
                        </p>
                        <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5" data-testid="fitness-baseline-label">
                          Latest session before {fitBaselineSeason?.name || 'current season'} ·{' '}
                          <span className="font-mono">{fitBaseline.session_date}</span>
                        </p>
                        <p className="text-[10px] text-amber-700/80 dark:text-amber-400/80 mt-1">
                          Reference only — not counted in current-season assessments.
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Latest Assessment Summary */}
                  <Card data-testid="athlete-latest-summary-card">
                    <CardHeader>
                      <CardTitle>Latest Assessment Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {!latestSession ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6"
                           data-testid="athlete-latest-empty">
                          No assessments recorded yet for this athlete.
                        </p>
                      ) : (
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          <Badge variant="outline" data-testid="latest-summary-date">
                            Session: {latestSession.session_date}
                          </Badge>
                          <Badge variant="outline" data-testid="latest-summary-results">
                            {latestSession.result_count || 0} test{(latestSession.result_count || 0) === 1 ? '' : 's'} recorded
                          </Badge>
                          {latestSession.evaluator_name && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Evaluator: {latestSession.evaluator_name}
                            </span>
                          )}
                          <Button
                            size="sm" variant="outline"
                            onClick={() => openViewSession(latestSession)}
                            data-testid="latest-summary-view-btn"
                          >
                            View Latest Session
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recent Sessions — FOR THIS ATHLETE ONLY */}
                  <Card data-testid="athlete-recent-sessions-card">
                    <CardHeader>
                      <CardTitle>Recent Assessments</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                        </div>
                      ) : athleteSessions.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-8"
                           data-testid="athlete-recent-empty">
                          No recent assessment sessions for this athlete.
                        </p>
                      ) : (
                        <div className="space-y-3" data-testid="athlete-sessions-list">
                          {athleteSessions.map(session => (
                            <div
                              key={session.id}
                              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                              onClick={() => openViewSession(session)}
                              data-testid={`session-${session.id}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold">
                                  {session.athlete_name?.charAt(0) || '?'}
                                </div>
                                <div>
                                  <p className="font-medium">{session.athlete_name}</p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">{session.session_date}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant="outline">{session.result_count || 0} results</Badge>
                                <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">
                                  by {session.evaluator_name || 'Unknown'}
                                </span>
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="sm" variant="ghost"
                                    className="h-8 w-8 p-0 text-gray-500 hover:text-emerald-600"
                                    onClick={() => openEditSession(session)}
                                    title="Edit assessment"
                                    data-testid={`edit-session-${session.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm" variant="ghost"
                                    className="h-8 w-8 p-0 text-gray-500 hover:text-red-600"
                                    onClick={() => handleDeleteSession(session)}
                                    title="Delete assessment"
                                    data-testid={`delete-session-${session.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Trends / Progress charts (athlete-scoped). */}
                  <Card data-testid="athlete-trends-card">
                    <CardHeader>
                      <CardTitle>Trends & Progress</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <FitnessProgressView athleteId={selectedAthlete} mode="full" />
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          );
        })()}

        {/* New Session Dialog */}
        <Dialog open={newSessionDialog} onOpenChange={setNewSessionDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Assessment Session</DialogTitle>
              <DialogDescription>Start a new physical assessment for an athlete</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Athlete *</Label>
                <SearchableAthleteSelect
                  athletes={athletes}
                  value={newSession.athlete_id}
                  onChange={v => setNewSession({...newSession, athlete_id: v})}
                  placeholder="Search & select athlete..."
                  data-testid="new-session-athlete"
                />
              </div>
              
              <div>
                <Label>Assessment Date *</Label>
                <Input 
                  type="date" 
                  value={newSession.session_date}
                  onChange={e => setNewSession({...newSession, session_date: e.target.value})}
                  data-testid="new-session-date"
                />
              </div>
              
              <div>
                <Label>Evaluator Notes</Label>
                <Textarea 
                  value={newSession.evaluator_notes}
                  onChange={e => setNewSession({...newSession, evaluator_notes: e.target.value})}
                  placeholder="General observations about this assessment session..."
                  data-testid="new-session-notes"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewSessionDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateSession} className="bg-pfap-green hover:bg-pfap-green/90">
                Create & Record Results
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Record Results Dialog */}
        <Dialog open={recordResultsDialog} onOpenChange={setRecordResultsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record Assessment Results</DialogTitle>
              <DialogDescription>
                {selectedSession?.athlete_name} - Enter test results for each category
              </DialogDescription>
            </DialogHeader>
            
            <Tabs
              defaultValue={
                Object.keys(categories).find((k) => !k.startsWith('_')) || ''
              }
              className="mt-4"
            >
              <TabsList className="mb-4 flex-wrap">
                {Object.keys(categories)
                  .filter((cat) => !cat.startsWith('_'))
                  .map(cat => (
                    <TabsTrigger
                      key={cat}
                      value={cat}
                      style={{ borderBottomColor: CATEGORY_COLORS[cat] || '#6b7280' }}
                      data-testid={`assessment-tab-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {cat}
                    </TabsTrigger>
                  ))}
              </TabsList>

              {Object.entries(categories)
                .filter(([category]) => !category.startsWith('_'))
                .map(([category, catData]) => (
                <TabsContent key={category} value={category}>
                  <div className="space-y-4">
                    {(catData?.tests || []).map(test => {
                      const unit = TEST_UNIT_OVERRIDE[test] || CATEGORY_UNIT[category] || catData?.unit || '';
                      const isLaps = LAPS_TESTS.has(test);
                      const isStrength = STRENGTH_CATEGORIES.has(category) && !isLaps;
                      const isTime = TIME_CATEGORIES.has(category) && !isLaps;
                      const isDistance = DISTANCE_CATEGORIES.has(category) && !isLaps;
                      const isMeasurement = MEASUREMENT_CATEGORIES.has(category) && !isLaps;
                      return (
                        <Card key={test} className="p-4 bg-white dark:bg-gray-950">
                          <h4
                            className="font-medium mb-3"
                            style={{ color: CATEGORY_COLORS[category] || '#111827' }}
                          >
                            {test}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {isStrength && (
                              <div>
                                <Label className="text-xs">Weight / Result ({unit})</Label>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  value={results[test]?.weight || ''}
                                  onChange={e => updateResult(test, 'weight', e.target.value)}
                                  placeholder="0"
                                  data-testid={`assessment-input-${test.toLowerCase().replace(/\s+/g, '-')}-weight`}
                                />
                              </div>
                            )}
                            {isTime && (
                              <div>
                                <Label className="text-xs">Result ({unit})</Label>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  value={results[test]?.time || ''}
                                  onChange={e => updateResult(test, 'time', e.target.value)}
                                  placeholder="0.00"
                                  data-testid={`assessment-input-${test.toLowerCase().replace(/\s+/g, '-')}-time`}
                                />
                              </div>
                            )}
                            {isMeasurement && (
                              <div>
                                <Label className="text-xs">Result ({unit})</Label>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  value={results[test]?.measurement || ''}
                                  onChange={e => updateResult(test, 'measurement', e.target.value)}
                                  placeholder="0"
                                  data-testid={`assessment-input-${test.toLowerCase().replace(/\s+/g, '-')}-measurement`}
                                />
                              </div>
                            )}
                            {isDistance && (
                              <div>
                                <Label className="text-xs">Result ({unit})</Label>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  value={results[test]?.distance || ''}
                                  onChange={e => updateResult(test, 'distance', e.target.value)}
                                  placeholder="0"
                                  data-testid={`assessment-input-${test.toLowerCase().replace(/\s+/g, '-')}-distance`}
                                />
                              </div>
                            )}
                            {isLaps && (
                              <div>
                                <Label className="text-xs">Result ({unit})</Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  value={results[test]?.laps || ''}
                                  onChange={e => updateResult(test, 'laps', e.target.value)}
                                  placeholder="0"
                                  className={
                                    results[test]?.laps && parseLapsSafe(results[test].laps) === null
                                      ? 'border-rose-400 focus-visible:ring-rose-400'
                                      : ''
                                  }
                                  data-testid={`assessment-input-${test.toLowerCase().replace(/\s+/g, '-')}-laps`}
                                />
                                {results[test]?.laps && parseLapsSafe(results[test].laps) === null && (
                                  <p
                                    className="text-[10px] text-rose-500 mt-1"
                                    data-testid={`assessment-input-${test.toLowerCase().replace(/\s+/g, '-')}-laps-error`}
                                  >
                                    Must be a whole number ≥ 0
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Phase-2 live Performance Category —
                                the backend classifier runs as the
                                user types (see schedulePreview). */}
                            <div>
                              <Label className="text-xs">Performance Category</Label>
                              <div className="mt-1">
                                <PerformanceBadge
                                  label={livePreviews[test]?.label || 'Pending Calculation'}
                                  colorKey={livePreviews[test]?.colorKey || 'gray'}
                                  reason={livePreviews[test]?.reason}
                                  testId={`assessment-perf-category-${test.toLowerCase().replace(/\s+/g, '-')}`}
                                  size="md"
                                />
                              </div>
                            </div>

                            <div>
                              <Label className="text-xs">Notes</Label>
                              <Input
                                value={results[test]?.notes || ''}
                                onChange={e => updateResult(test, 'notes', e.target.value)}
                                placeholder="Optional notes"
                              />
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setRecordResultsDialog(false)}>Cancel</Button>
              <Button onClick={handleRecordResults} className="bg-pfap-green hover:bg-pfap-green/90">
                Save Results
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Session Dialog */}
        <Dialog open={viewSessionDialog} onOpenChange={setViewSessionDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assessment Session Details</DialogTitle>
              <DialogDescription>
                {selectedSession?.athlete?.full_name} - {selectedSession?.session_date}
              </DialogDescription>
            </DialogHeader>
            
            {selectedSession?.results && (
              <div className="space-y-4 mt-4">
                {Object.entries(selectedSession.results).map(([category, results]) => (
                  results.length > 0 && (
                    <Card key={category}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm" style={{ color: CATEGORY_COLORS[category] }}>
                          {category}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {results.map(result => {
                            const classifyContextMissing = (result.performance_label === 'Unclassified') &&
                              ((result.performance_reason || '').toLowerCase().includes('gender'));
                            return (
                            <div key={result.id} className="bg-gray-50 dark:bg-gray-900 p-3 rounded" data-testid={`assessment-result-${result.id}`}>
                              <p className="text-sm font-medium">{result.test_type}</p>
                              <p className="text-lg font-bold" style={{ color: CATEGORY_COLORS[category] }}>
                                {getResultValue(result)}
                              </p>
                              <div className="mt-1">
                                <PerformanceBadge
                                  label={result.performance_label || 'Unclassified'}
                                  colorKey={result.performance_color || 'gray'}
                                  reason={result.performance_reason || result.threshold_summary}
                                />
                              </div>
                              {/* Classification context (Phase 67) */}
                              {(result.season_age_snapshot != null || result.gender_snapshot || selectedSession?.session_date) && (
                                <div
                                  className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed"
                                  data-testid={`classification-context-${result.id}`}
                                >
                                  <div>Age at test: <span className="text-gray-700 dark:text-gray-300">{result.season_age_snapshot ?? '—'}</span></div>
                                  <div>Gender: <span className="text-gray-700 dark:text-gray-300">{result.gender_snapshot || '—'}</span></div>
                                  <div>Test date: <span className="text-gray-700 dark:text-gray-300">{selectedSession?.session_date || '—'}</span></div>
                                </div>
                              )}
                              {classifyContextMissing && (
                                <p
                                  className="mt-1 text-[11px] text-amber-600 dark:text-amber-400"
                                  data-testid={`gender-missing-hint-${result.id}`}
                                >
                                  Gender not set — standard classification may be unavailable.
                                </p>
                              )}
                              {result.notes && (
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{result.notes}</p>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )
                ))}
                
                {selectedSession.evaluator_notes && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Evaluator Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 dark:text-gray-300">{selectedSession.evaluator_notes}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StaffAssessments;
